import { motion } from "framer-motion";
import { EASE, Magnetic } from "./ui/motion";
import { Panel, Parallax } from "../journey/Panel";
import { scrollToSection } from "../journey/Journey";

const EASE_LETTER = [0.16, 1, 0.3, 1];

function Word({ text, className = "", delay = 0, italic = false }) {
  return (
    <span className="inline-flex overflow-hidden">
      {text.split("").map((ch, i) => (
        <motion.span
          key={i}
          className={`inline-block ${italic ? "italic" : ""} ${className}`}
          initial={{ y: "110%" }}
          animate={{ y: 0 }}
          transition={{ duration: 1.1, delay: delay + i * 0.04, ease: EASE_LETTER }}
        >
          {ch}
        </motion.span>
      ))}
    </span>
  );
}

function SpinningBadge() {
  const text = "SCROLL TO EXPLORE · SCROLL TO EXPLORE · ";
  return (
    <div className="relative w-28 h-28">
      <motion.svg
        viewBox="0 0 100 100"
        className="w-full h-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 16, repeat: Infinity, ease: "linear" }}
      >
        <defs>
          <path id="circlePath" d="M50,50 m-37,0 a37,37 0 1,1 74,0 a37,37 0 1,1 -74,0" />
        </defs>
        <text className="fill-paper-dim font-mono" style={{ fontSize: "7.2px", letterSpacing: "1.5px" }}>
          <textPath href="#circlePath">{text}</textPath>
        </text>
      </motion.svg>
      <span className="absolute inset-0 flex items-center justify-center text-acid text-xl">↓</span>
    </div>
  );
}

export default function Hero({ ready, horizontal }) {
  return (
    <section id="top" className={horizontal ? "flex h-full shrink-0" : "relative"}>
      <Panel
        horizontal={horizontal}
        className={
          horizontal
            ? "flex flex-col justify-center px-6 sm:px-10 pt-24 pb-12"
            : "min-h-screen flex flex-col justify-center px-6 sm:px-10 pt-28 pb-16"
        }
      >
        <div className="flex flex-col">
          {/* top meta row */}
          <motion.div
            className="flex flex-wrap items-center justify-between gap-4 mb-8 sm:mb-12"
            initial={{ opacity: 0 }}
            animate={ready ? { opacity: 1 } : {}}
            transition={{ delay: 0.4, duration: 1 }}
          >
            <p className="eyebrow max-w-xs leading-relaxed">
              Front-End Engineer
              <br />
              &amp; Creative Coder
            </p>
            <p className="eyebrow text-right">
              Based in Naousa, Greece
              <br />
              <span className="text-acid">● Open to opportunities</span>
            </p>
          </motion.div>

          {/* giant name */}
          <Parallax horizontal={horizontal} depth={-0.06}>
            {ready && (
              <h1 className="display leading-[0.82] text-paper select-none">
                <span className="block text-[18vw] mdl:text-[13vw]">
                  <Word text="TSIAKIRIS" className="text-stroke" delay={0.3} />
                </span>
                <span className="block text-[18vw] mdl:text-[13vw] text-right">
                  <Word text="Nikos" italic className="text-acid font-light" delay={0.6} />
                </span>
              </h1>
            )}
          </Parallax>

          {/* bottom row */}
          <motion.div
            className="mt-10 sm:mt-14 flex flex-col mdl:flex-row items-start mdl:items-end justify-between gap-8"
            initial={{ opacity: 0, y: 30 }}
            animate={ready ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 1, duration: 1, ease: EASE }}
          >
            <p className="max-w-md text-paper-dim text-base sm:text-lg leading-relaxed">
              Currently a Front-End Engineer building production apps with Vue
              &amp; C#. I sweat the small stuff — motion, performance, and the
              details most people skip.
            </p>

            <div className="flex items-center gap-8">
              <Magnetic strength={0.5}>
                <a
                  href="#projects"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("projects");
                  }}
                  data-cursor-label="view"
                  className="group inline-flex items-center gap-3 rounded-full border border-paper/30 px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-paper hover:bg-acid hover:text-ink hover:border-acid transition-colors duration-300"
                >
                  Selected Work
                  <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                </a>
              </Magnetic>
              <div className="hidden sm:block">
                <SpinningBadge />
              </div>
            </div>
          </motion.div>
        </div>
      </Panel>
    </section>
  );
}
